/*
 * composite-zoom — Amazon-style click-to-zoom for product gallery images.
 *
 * Why this is custom: each product picture is really several stacked layers
 * inside .product-media-container — the garment photo plus, on a designed
 * product, the generated course map (mix-blend overlay) and its live title SVG.
 * Dawn's built-in lightbox/hover zoom only magnifies the base <img>, so the
 * overlay would be left behind. Here we scale a single frame that wraps ALL the
 * layers and clip it to the media box, so the composite zooms as one piece.
 *
 * Interaction: click a picture to zoom in at that point; move the pointer to pan;
 * click again (or move off the image) to zoom back out. Fine-pointer (mouse)
 * only — touch devices keep their native pinch-zoom.
 *
 * Wiring: zoom frames are marked [data-zoom-target] and their parent element is
 * the clip viewport (the media box). Everything is event-delegated from the
 * document so it keeps working after a colour swap replaces the gallery DOM.
 */
(function () {
  'use strict';

  // Mouse only. On touch we leave the browser's native pinch-zoom alone.
  if (!window.matchMedia || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var SCALE = 2.5;
  var active = null; // { target, viewport, onMove, onLeave }

  // Pointer position expressed as a transform-origin (% of the un-transformed
  // box). The viewport is never scaled and the target fills it at rest, so the
  // viewport's rect stays a stable reference even while the target is magnified.
  function originAt(viewport, e) {
    var r = viewport.getBoundingClientRect();
    var x = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1) * 100;
    var y = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1) * 100;
    return x + '% ' + y + '%';
  }

  function exit() {
    if (!active) return;
    var a = active;
    active = null;
    a.viewport.removeEventListener('pointermove', a.onMove);
    a.viewport.removeEventListener('pointerleave', a.onLeave);
    a.target.classList.remove('is-zoomed');
    a.target.style.transform = '';
    // Keep the box clipped until the zoom-out has animated back, unless a fresh
    // zoom has meanwhile taken over the same viewport.
    window.setTimeout(function () {
      if (!active || active.viewport !== a.viewport) {
        a.viewport.classList.remove('composite-zoom-clip');
      }
    }, 280);
  }

  // The photo's srcset was chosen for its at-rest size, so magnifying it would
  // just stretch those pixels. Telling the browser the image is now SCALE times
  // wider makes it fetch a larger srcset candidate. Left raised after zoom-out:
  // the big file is already cached, and the browser never swaps back down anyway.
  function upgradeResolution(target) {
    var imgs = target.querySelectorAll('img[srcset]');
    for (var i = 0; i < imgs.length; i++) {
      // offsetWidth, not the bounding rect: it ignores transforms, so a frame
      // still animating out of a previous zoom doesn't inflate the figure.
      var w = Math.ceil(imgs[i].offsetWidth * SCALE);
      if (w > 0 && imgs[i].dataset.zoomSizes !== String(w)) {
        imgs[i].dataset.zoomSizes = String(w);
        imgs[i].sizes = w + 'px';
      }
    }
  }

  function enter(target, e) {
    var viewport = target.parentElement;
    if (!viewport) return;
    upgradeResolution(target);
    var onMove = function (ev) { target.style.transformOrigin = originAt(viewport, ev); };
    var onLeave = function () { exit(); };
    active = { target: target, viewport: viewport, onMove: onMove, onLeave: onLeave };
    viewport.classList.add('composite-zoom-clip');
    target.style.transformOrigin = originAt(viewport, e);
    target.classList.add('is-zoomed');
    // Set scale after the origin so the zoom-in animates outward from the click.
    target.style.transform = 'scale(' + SCALE + ')';
    viewport.addEventListener('pointermove', onMove);
    viewport.addEventListener('pointerleave', onLeave);
  }

  document.addEventListener('click', function (e) {
    var target = e.target.closest ? e.target.closest('[data-zoom-target]') : null;
    if (active) {
      var was = active.target;
      exit();
      // Click on a different picture: zoom that one instead of just exiting.
      if (target && target !== was) enter(target, e);
      return;
    }
    if (target) enter(target, e);
  });
})();
